import { ContentSourceDetailsFragment } from '~/graphqlTypes/ContentSourceDetailsFragment';
import { ContentSourceRightsMappingFragment } from '~/graphqlTypes/ContentSourceRightsMappingFragment';
import { useAsyncModal } from '~/hooks/useAsyncModal';
import type * as UseAsyncModal from '~/hooks/useAsyncModal';

type ModelType = ContentSourceRightsMappingFragment;

export const findMatchingMapping = (
  source: null | ContentSourceDetailsFragment,
  commercialModelType: undefined | string,
  useType: undefined | string,
  existingId?: string,
): ContentSourceRightsMappingFragment | undefined =>
  source?.rightsMappings.find(
    ( mapping ) =>
      // We have commercial model and use types
      commercialModelType &&
      useType &&
      // And we have a mapping that we are not currently editing
      ( !existingId || mapping.id !== existingId ) &&
      // And its commercial model type matches, with wildcard support
      ( mapping.commercialModelType === commercialModelType ||
        commercialModelType === '*' ||
        mapping.commercialModelType === '*' ) &&
      // And its use type matches, with wildcard support
      ( mapping.useType === useType || useType === '*' || mapping.useType === '*' ),
  );

const isModelComplete =
  ( source: ContentSourceDetailsFragment ) =>
    ( sparseModel?: Partial<ContentSourceRightsMappingFragment> ): sparseModel is ContentSourceRightsMappingFragment =>
      sparseModel !== undefined &&
    sparseModel.commercialModelType !== undefined &&
    sparseModel.useType !== undefined &&
    sparseModel.streamingRights !== undefined &&
    sparseModel.streamingRights.length > 0 &&
    !findMatchingMapping( source, sparseModel.commercialModelType, sparseModel.useType, sparseModel.id );

export function useRightsMappingModal( source: ContentSourceDetailsFragment ): UseAsyncModal.AsyncModal<ModelType> {
  const { show: baseShow, showing, controls } = useAsyncModal<ModelType>();

  const show = ( config: UseAsyncModal.Configuration<ModelType> ) =>
    baseShow( {
      isModelComplete: isModelComplete( source ),
      ...config,
    } );

  const hook: UseAsyncModal.AsyncModal<ModelType> = showing ? { show, showing, controls } : { show, showing };

  return hook;
}
